import { getDabUrl } from './api';

// Server-side interface (matches database schema)
export interface IncidentReportServer {
  id?: number;
  owner_user_id?: string;
  owner_email?: string;
  owner_name?: string | null;
  incident_datetime: string; // ISO datetime
  location: string;
  travellers_involved: string;

  // Incident types (bit flags)
  incident_type_medical: boolean;
  incident_type_security: boolean;
  incident_type_accident: boolean;
  incident_type_near_miss: boolean;
  incident_type_other: boolean;
  incident_type_other_details?: string | null;

  // Description
  description: string;
  immediate_actions?: string | null;

  // Parties notified (bit flags)
  notified_duty_officer: boolean;
  notified_security_focal_point: boolean;
  notified_md: boolean;
  notified_client: boolean;
  notified_aig_insurer: boolean;

  // Follow-up
  followup_actions?: string | null;

  // Completion
  completed_by: string;
  completed_date: string; // ISO date

  // Status
  status: 'draft' | 'submitted' | 'under_review' | 'closed';

  // Timestamps
  created_at?: string;
  last_modified?: string;

  // Optional link to travel form
  travel_form_id?: number | null;
}

// Client-side interface (camelCase for React)
export interface IncidentReportClient {
  id?: number;
  ownerUserId?: string;
  ownerEmail?: string;
  ownerName?: string;
  incidentDateTime: string;
  location: string;
  travellersInvolved: string;

  // Incident types
  incidentTypeMedical: boolean;
  incidentTypeSecurity: boolean;
  incidentTypeAccident: boolean;
  incidentTypeNearMiss: boolean;
  incidentTypeOther: boolean;
  incidentTypeOtherDetails: string;

  // Description
  description: string;
  immediateActions: string;

  // Parties notified
  notifiedDutyOfficer: boolean;
  notifiedSecurityFocalPoint: boolean;
  notifiedMd: boolean;
  notifiedClient: boolean;
  notifiedAigInsurer: boolean;

  // Follow-up
  followupActions: string;

  // Completion
  completedBy: string;
  completedDate: string;

  // Status
  status: 'draft' | 'submitted' | 'under_review' | 'closed';

  // Timestamps
  createdAt?: string;
  lastModified?: string;

  // Optional link to travel form
  travelFormId?: number | null;
}

/**
 * Convert server format to client format
 */
export function fromServerIncidentReport(server: IncidentReportServer): IncidentReportClient {
  return {
    id: server.id,
    ownerUserId: server.owner_user_id,
    ownerEmail: server.owner_email,
    ownerName: server.owner_name || undefined,
    incidentDateTime: server.incident_datetime,
    location: server.location,
    travellersInvolved: server.travellers_involved,

    incidentTypeMedical: server.incident_type_medical,
    incidentTypeSecurity: server.incident_type_security,
    incidentTypeAccident: server.incident_type_accident,
    incidentTypeNearMiss: server.incident_type_near_miss,
    incidentTypeOther: server.incident_type_other,
    incidentTypeOtherDetails: server.incident_type_other_details || '',

    description: server.description,
    immediateActions: server.immediate_actions || '',

    notifiedDutyOfficer: server.notified_duty_officer,
    notifiedSecurityFocalPoint: server.notified_security_focal_point,
    notifiedMd: server.notified_md,
    notifiedClient: server.notified_client,
    notifiedAigInsurer: server.notified_aig_insurer,

    followupActions: server.followup_actions || '',

    completedBy: server.completed_by,
    completedDate: server.completed_date,

    status: server.status,
    createdAt: server.created_at,
    lastModified: server.last_modified,
    travelFormId: server.travel_form_id,
  };
}

/**
 * Convert client format to server format
 */
export function toServerIncidentReport(
  client: IncidentReportClient,
  ownerUserId: string,
  ownerEmail: string,
  ownerName: string
): IncidentReportServer {
  return {
    id: client.id,
    owner_user_id: ownerUserId,
    owner_email: ownerEmail,
    owner_name: ownerName,
    incident_datetime: client.incidentDateTime,
    location: client.location,
    travellers_involved: client.travellersInvolved,

    incident_type_medical: client.incidentTypeMedical,
    incident_type_security: client.incidentTypeSecurity,
    incident_type_accident: client.incidentTypeAccident,
    incident_type_near_miss: client.incidentTypeNearMiss,
    incident_type_other: client.incidentTypeOther,
    incident_type_other_details: client.incidentTypeOtherDetails || null,

    description: client.description,
    immediate_actions: client.immediateActions || null,

    notified_duty_officer: client.notifiedDutyOfficer,
    notified_security_focal_point: client.notifiedSecurityFocalPoint,
    notified_md: client.notifiedMd,
    notified_client: client.notifiedClient,
    notified_aig_insurer: client.notifiedAigInsurer,

    followup_actions: client.followupActions || null,

    completed_by: client.completedBy,
    completed_date: client.completedDate,

    status: client.status,
    travel_form_id: client.travelFormId || null,
  };
}

/**
 * Fetch all incident reports for the current user
 */
export async function fetchIncidentReports(): Promise<IncidentReportClient[]> {
  const response = await fetch(getDabUrl('IncidentReports'), {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch incident reports: ${response.statusText}`);
  }

  const data = await response.json();
  const reports = data.value || [];
  return reports.map(fromServerIncidentReport);
}

/**
 * Fetch a single incident report by ID
 */
export async function fetchIncidentReportById(id: number): Promise<IncidentReportClient | null> {
  const response = await fetch(getDabUrl(`IncidentReports/id/${id}`), {
    credentials: 'include',
  });

  if (!response.ok) {
    if (response.status === 404) return null;
    throw new Error(`Failed to fetch incident report: ${response.statusText}`);
  }

  const data = await response.json();
  return fromServerIncidentReport(data);
}

/**
 * Create a new incident report
 */
export async function createIncidentReport(
  report: IncidentReportClient,
  ownerUserId: string,
  ownerEmail: string,
  ownerName: string
): Promise<IncidentReportClient> {
  const serverReport = toServerIncidentReport(report, ownerUserId, ownerEmail, ownerName);

  const response = await fetch(getDabUrl('IncidentReports'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(serverReport),
  });

  if (!response.ok) {
    throw new Error(`Failed to create incident report: ${response.statusText}`);
  }

  const data = await response.json();
  return fromServerIncidentReport(data);
}

/**
 * Update an existing incident report
 */
export async function updateIncidentReport(
  report: IncidentReportClient,
  ownerUserId: string,
  ownerEmail: string,
  ownerName: string
): Promise<IncidentReportClient> {
  if (!report.id) {
    throw new Error('Cannot update incident report without an ID');
  }

  const serverReport = toServerIncidentReport(report, ownerUserId, ownerEmail, ownerName);

  const response = await fetch(getDabUrl(`IncidentReports/id/${report.id}`), {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(serverReport),
  });

  if (!response.ok) {
    throw new Error(`Failed to update incident report: ${response.statusText}`);
  }

  const data = await response.json();
  return fromServerIncidentReport(data);
}

/**
 * Delete an incident report
 */
export async function deleteIncidentReport(id: number): Promise<void> {
  const response = await fetch(getDabUrl(`IncidentReports/id/${id}`), {
    method: 'DELETE',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to delete incident report: ${response.statusText}`);
  }
}

/**
 * Save incident report (create or update)
 */
export async function saveIncidentReport(
  report: IncidentReportClient,
  ownerUserId: string,
  ownerEmail: string,
  ownerName: string
): Promise<IncidentReportClient> {
  if (report.id) {
    return updateIncidentReport(report, ownerUserId, ownerEmail, ownerName);
  } else {
    return createIncidentReport(report, ownerUserId, ownerEmail, ownerName);
  }
}
